import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, ShieldIcon, PlusIcon, XIcon, LockIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiClient, RolePermissionEntry, UpdateRolePermissionsRequest } from '../lib/api'

const PERMISSION_LEVELS = ['full', 'read-only', 'disabled', 'hidden'] as const

const PERMISSION_COLORS: Record<string, string> = {
  full: 'bg-green-100 text-green-800',
  'read-only': 'bg-blue-100 text-blue-800',
  disabled: 'bg-yellow-100 text-yellow-800',
  hidden: 'bg-red-100 text-red-800',
}

const ROLE_LABELS: Record<string, { label: string; description: string }> = {
  super_admin: { label: 'Super Admin', description: 'Platform-level administrator with full access. Cannot be modified.' },
  admin: { label: 'Admin', description: 'Tenant administrator with configurable access levels.' },
  manager: { label: 'Manager', description: 'Tenant manager with configurable access levels.' },
  user: { label: 'User', description: 'Regular tenant user with configurable access levels.' },
}

const ROLES_ORDER = ['super_admin', 'admin', 'manager', 'user'] as const

// Known routes/sections for autocomplete suggestions
const KNOWN_ROUTES = [
  '/',
  '/dashboard/engagement',
  '/dashboard/aeo',
  '/conversations',
  '/knowledge-base/documents',
  '/knowledge-base/products',
  '/knowledge-base/services',
  '/knowledge-base/embeddings',
  '/content-management/assets',
  '/content-management/questions',
  '/content-management/ctas',
  '/content-management/reviews',
  '/content-management/homepage',
  '/settings',
  '/settings/advanced',
  '/keys',
  '/cta-clicks',
  '/engagement-activity/forms',
  '/engagement-activity/leads',
  '/engagement-activity/events',
  '/appointments',
  '/message-text',
  '/expert-agents/experts',
  '/expert-agents/flagged-answers',
  '/expert-agents/training',
  '/expert-agents/settings',
  'settings:configuration',
  'settings:integrations',
  'settings:guardrails',
  'settings:widget',
  'settings:aeo',
  'settings:contacts',
  'settings:appointment',
  'settings:advanced',
]

// Internal UI state — separated for clean editing
interface EditableRole {
  default: string
  overrides: Record<string, string>
}

// Convert flat API entry → internal editable format
function fromFlat(entry: RolePermissionEntry): EditableRole {
  const { default: defaultPerm, ...overrides } = entry
  return { default: defaultPerm || 'full', overrides }
}

// Convert internal editable format → flat API entry
function toFlat(editable: EditableRole): RolePermissionEntry {
  return { default: editable.default, ...editable.overrides }
}

export default function RolePermissions() {
  const queryClient = useQueryClient()
  const [editState, setEditState] = useState<Record<string, EditableRole>>({})
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [newOverrideKey, setNewOverrideKey] = useState<Record<string, string>>({})
  const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({
    admin: false,
    manager: false,
    user: false,
  })

  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['rolePermissions'],
    queryFn: apiClient.getRolePermissions,
  })

  // Sync fetched flat data into internal edit state
  useEffect(() => {
    if (permissions) {
      const state: Record<string, EditableRole> = {}
      for (const role of ['admin', 'manager', 'user'] as const) {
        if (permissions[role]) {
          state[role] = fromFlat(permissions[role])
        }
      }
      setEditState(state)
    }
  }, [permissions])

  const updateMutation = useMutation({
    mutationFn: apiClient.updateRolePermissions,
    onSuccess: (data) => {
      queryClient.setQueryData(['rolePermissions'], data)
      setMessage({ type: 'success', text: 'Role permissions updated successfully!' })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (error: Error) => {
      setMessage({ type: 'error', text: error.message || 'Failed to update role permissions' })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  const handleSave = () => {
    const payload: UpdateRolePermissionsRequest = {}
    for (const role of ['admin', 'manager', 'user'] as const) {
      if (editState[role]) {
        payload[role] = toFlat(editState[role])
      }
    }
    updateMutation.mutate(payload)
  }

  const handleDefaultChange = (role: string, value: string) => {
    setEditState((prev) => ({
      ...prev,
      [role]: { ...prev[role], default: value },
    }))
  }

  const handleOverrideChange = (role: string, key: string, value: string) => {
    setEditState((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        overrides: { ...prev[role].overrides, [key]: value },
      },
    }))
  }

  const handleRemoveOverride = (role: string, key: string) => {
    setEditState((prev) => {
      const newOverrides = { ...prev[role].overrides }
      delete newOverrides[key]
      return {
        ...prev,
        [role]: { ...prev[role], overrides: newOverrides },
      }
    })
  }

  const handleAddOverride = (role: string) => {
    const key = newOverrideKey[role]?.trim()
    if (!key) return
    if (editState[role]?.overrides[key] !== undefined) return

    setEditState((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        overrides: { ...prev[role].overrides, [key]: 'read-only' },
      },
    }))
    setNewOverrideKey((prev) => ({ ...prev, [role]: '' }))
  }

  const toggleExpand = (role: string) => {
    setExpandedRoles((prev) => ({ ...prev, [role]: !prev[role] }))
  }

  const hasChanges = () => {
    if (!permissions) return false
    for (const role of ['admin', 'manager', 'user'] as const) {
      const original = permissions[role]
      const edited = editState[role]
      if (!original || !edited) continue
      const origParsed = fromFlat(original)
      if (origParsed.default !== edited.default) return true
      const origKeys = Object.keys(origParsed.overrides)
      const editKeys = Object.keys(edited.overrides)
      if (origKeys.length !== editKeys.length) return true
      for (const k of origKeys) {
        if (origParsed.overrides[k] !== edited.overrides[k]) return true
      }
    }
    return false
  }

  // Helper to get display data for a role (works for both super_admin and editable roles)
  const getRoleDisplayData = (role: string) => {
    if (role === 'super_admin' && permissions?.super_admin) {
      return fromFlat(permissions.super_admin)
    }
    return editState[role]
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600">Loading role permissions...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Failed to load role permissions. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Breadcrumb Navigation */}
      <div className="mb-4">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Settings
        </Link>
        <nav className="flex mt-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/settings" className="text-gray-500 hover:text-gray-700">
                Settings
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-gray-500 md:ml-2">Role Permissions</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <div className="flex items-center justify-between">
        <div className="">
          <h1 className="text-2xl font-bold text-gray-900">Role Permissions</h1>
          <p className="text-gray-600 mt-2">
            Manage access levels for each user role across the platform
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={updateMutation.isLoading || !hasChanges()}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white shadow-sm ${hasChanges()
            ? 'bg-indigo-600 hover:bg-indigo-700'
            : 'bg-gray-300 cursor-not-allowed'
            }`}
        >
          {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`rounded-md p-4 ${message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
            }`}
        >
          {message.text}
        </div>
      )}

      {/* Permission level legend */}
      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Permission Levels</h3>
        <div className="flex flex-wrap gap-3">
          {PERMISSION_LEVELS.map((level) => (
            <span
              key={level}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${PERMISSION_COLORS[level]}`}
            >
              {level}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          <strong>full</strong> = full access &middot; <strong>read-only</strong> = can view, writes blocked &middot;{' '}
          <strong>disabled</strong> = visible but greyed out &middot; <strong>hidden</strong> = removed from menu
        </p>
      </div>

      {/* Role cards */}
      {ROLES_ORDER.map((role) => {
        const isSuperAdmin = role === 'super_admin'
        const roleInfo = ROLE_LABELS[role]
        const roleData = getRoleDisplayData(role)
        const isExpanded = expandedRoles[role]
        const overrides = roleData?.overrides || {}
        const overrideKeys = Object.keys(overrides).sort()

        return (
          <div
            key={role}
            className={`bg-white shadow rounded-lg overflow-hidden ${isSuperAdmin ? 'opacity-75' : ''
              }`}
          >
            {/* Role header */}
            <div
              className={`px-6 py-4 flex items-center justify-between ${isSuperAdmin ? 'bg-gray-50' : 'bg-white cursor-pointer hover:bg-gray-50'
                }`}
              onClick={() => !isSuperAdmin && toggleExpand(role)}
            >
              <div className="flex items-center space-x-3">
                {isSuperAdmin ? (
                  <LockIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ShieldIcon className="h-5 w-5 text-indigo-500" />
                )}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{roleInfo.label}</h3>
                  <p className="text-sm text-gray-500">{roleInfo.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${PERMISSION_COLORS[roleData?.default || 'full']
                    }`}
                >
                  default: {roleData?.default || 'full'}
                </span>
                {!isSuperAdmin && (
                  <span className="text-sm text-gray-400">
                    {overrideKeys.length} override{overrideKeys.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Expanded content (not for super_admin) */}
            {!isSuperAdmin && isExpanded && (
              <div className="px-6 py-4 border-t border-gray-200">
                {/* Default permission */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Permission
                  </label>
                  <select
                    value={roleData?.default || 'full'}
                    onChange={(e) => handleDefaultChange(role, e.target.value)}
                    className="block w-full max-w-xs rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  >
                    {PERMISSION_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Applied to all routes/sections unless overridden below
                  </p>
                </div>

                {/* Route overrides */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Route & Section Overrides
                    </label>
                  </div>

                  {overrideKeys.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {overrideKeys.map((key) => (
                        <div
                          key={key}
                          className="flex items-center space-x-3 bg-gray-50 rounded-md px-3 py-2"
                        >
                          <code className="flex-1 text-sm font-mono text-gray-700 truncate">
                            {key}
                          </code>
                          <select
                            value={overrides[key]}
                            onChange={(e) =>
                              handleOverrideChange(role, key, e.target.value)
                            }
                            className="rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 text-sm px-2 py-1"
                          >
                            {PERMISSION_LEVELS.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleRemoveOverride(role, key)}
                            className="text-red-400 hover:text-red-600"
                            title="Remove override"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">
                      No overrides — all routes use the default permission.
                    </p>
                  )}

                  {/* Add new override */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      list={`route-suggestions-${role}`}
                      placeholder="e.g. /keys or settings:advanced"
                      value={newOverrideKey[role] || ''}
                      onChange={(e) =>
                        setNewOverrideKey((prev) => ({
                          ...prev,
                          [role]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddOverride(role)
                        }
                      }}
                      className="flex-1 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 text-sm px-3 py-2"
                    />
                    <datalist id={`route-suggestions-${role}`}>
                      {KNOWN_ROUTES
                        .filter((route) => !overrideKeys.includes(route))
                        .map((route) => (
                          <option key={route} value={route} />
                        ))}
                    </datalist>
                    <button
                      onClick={() => handleAddOverride(role)}
                      disabled={!newOverrideKey[role]?.trim()}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
